import { MapLocation } from '@/types/location';
import { PopupHeader } from './marker/PopupHeader';
import { PopupDetails } from './marker/PopupDetails';
import { ScreenSelector } from './marker/ScreenSelector';
import { ActionButtons } from './marker/ActionButtons';
import { Button } from '@/components/ui/button';
import { Image, X } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from "@/contexts/LanguageContext";
import { ScrollArea } from '@/components/ui/scroll-area';

interface LocationDetailPanelProps {
  location: MapLocation | null;
  isSelected: boolean;
  selectedScreenCount: number;
  onClose: () => void;
  onSelect: (locationId: number) => void;
  onScreenCountChange?: (locationId: number, screenCount: number) => void;
  isMobile?: boolean;
}

export const LocationDetailPanel = ({
  location,
  isSelected,
  selectedScreenCount,
  onClose,
  onSelect,
  onScreenCountChange,
  isMobile = false
}: LocationDetailPanelProps) => {
  const { t } = useLanguage();
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [localScreenCount, setLocalScreenCount] = useState(selectedScreenCount);

  // Mobile sheet gesture state
  const [expanded, setExpanded] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartY = useRef<number | null>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    setLocalScreenCount(selectedScreenCount);
  }, [selectedScreenCount]);

  // Reset sheet state whenever a new location is focused
  useEffect(() => {
    setExpanded(false);
    setDragOffset(0);
    dragStartY.current = null;
    isDragging.current = false;
  }, [location?.id]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragStartY.current = e.clientY;
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    setDragOffset(delta > 0 ? delta : Math.max(delta, -60));
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const delta = dragOffset;
    dragStartY.current = null;
    setDragOffset(0);

    if (delta > 110) {
      onClose();
    } else if (delta < -30) {
      setExpanded(true);
    } else if (delta > 40) {
      setExpanded(false);
    }
  }, [dragOffset, onClose]);

  if (!location) return null;

  const isAttentionLocation = location.mediaOwner === 'Attention';
  const isOutdoorLocation = location.mediaKind === 'Outdoor';
  const hasPartialSelection = isAttentionLocation && selectedScreenCount > 0 && selectedScreenCount < location.devices;
  const hasMultipleScreens = location.devices > 1;
  const referenceImagePath = location.details?.referenceImage;
  const hasReferenceImage = !!referenceImagePath;

  const handleSelectButtonClick = () => {
    if (isAttentionLocation) {
      if (isSelected) {
        onSelect(location.id);
        if (onScreenCountChange) onScreenCountChange(location.id, 0);
      } else {
        onSelect(location.id);
        if (onScreenCountChange) onScreenCountChange(location.id, 1);
      }
    } else {
      onSelect(location.id);
    }
  };

  const handleScreenCountChange = (value: number[]) => {
    const newCount = value[0];
    setLocalScreenCount(newCount);
    if (onScreenCountChange) onScreenCountChange(location.id, newCount);
  };

  const incrementScreens = () => {
    if (localScreenCount < location.devices) {
      const newCount = localScreenCount + 1;
      setLocalScreenCount(newCount);
      if (onScreenCountChange) onScreenCountChange(location.id, newCount);
    }
  };

  const decrementScreens = () => {
    if (localScreenCount > 1) {
      const newCount = localScreenCount - 1;
      setLocalScreenCount(newCount);
      if (onScreenCountChange) onScreenCountChange(location.id, newCount);
    }
  };

  const panelContent = (
    <>
      {/* Gradient accent bar */}
      {!isMobile && (
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/70 to-primary/30 flex-shrink-0 rounded-t-2xl" />
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 flex-shrink-0">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Location Details</span>
        <button
          onClick={onClose}
          className={`${isMobile ? 'w-10 h-10' : 'w-7 h-7'} rounded-full bg-muted/60 hover:bg-muted flex items-center justify-center transition-colors`}
          aria-label="Close panel"
        >
          <X className={`${isMobile ? 'h-5 w-5' : 'h-3.5 w-3.5'} text-muted-foreground`} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 relative overflow-hidden min-h-0">
        <ScrollArea className="h-full">
          <div className={`px-4 ${isMobile ? 'pb-6' : 'pb-5'} space-y-4`}>
            <PopupHeader
              location={location}
              isSelected={isSelected}
              selectedScreenCount={selectedScreenCount}
              hasPartialSelection={hasPartialSelection}
            />

            {(!isMobile || expanded) && <PopupDetails location={location} />}

            {hasReferenceImage && (!isMobile || expanded) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReferenceModal(true)}
                className={`w-full flex items-center gap-2 rounded-xl border-border/60 hover:bg-muted/50 ${isMobile ? 'h-11 text-sm' : ''}`}
              >
                <Image className="h-4 w-4" />
                {t("viewReferenceImage")}
              </Button>
            )}

            {isMobile && !expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="w-full text-xs font-medium text-primary py-1"
              >
                View details
              </button>
            )}

            {isAttentionLocation ? (
              <div className="space-y-3">
                {isSelected && hasMultipleScreens && (
                  <ScreenSelector
                    localScreenCount={localScreenCount}
                    maxDevices={location.devices}
                    onIncrement={incrementScreens}
                    onDecrement={decrementScreens}
                    onSliderChange={handleScreenCountChange}
                    isOutdoorLocation={isOutdoorLocation}
                    isMobile={isMobile}
                  />
                )}
                <ActionButtons
                  location={location}
                  isSelected={isSelected}
                  onSelectButtonClick={handleSelectButtonClick}
                  isMobile={isMobile}
                />
              </div>
            ) : (
              <ActionButtons
                location={location}
                isSelected={isSelected}
                onSelectButtonClick={handleSelectButtonClick}
                isMobile={isMobile}
              />
            )}
          </div>
        </ScrollArea>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
      </div>

      {/* Reference image modal */}
      {hasReferenceImage && showReferenceModal && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-[100]">
          <div className="bg-card rounded-2xl p-4 max-w-3xl max-h-[90vh] overflow-auto border border-border/60 m-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-foreground">{t("locationReferenceImage")}</h3>
              <button
                onClick={() => setShowReferenceModal(false)}
                className="w-9 h-9 rounded-full bg-muted/60 hover:bg-muted flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <img
              src={referenceImagePath}
              alt="Location reference"
              className="w-full h-auto rounded-xl"
            />
          </div>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        {/* Scrim */}
        <div
          className="fixed inset-0 z-40 bg-background/40 backdrop-brightness-95 animate-in fade-in duration-200"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          className={`fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border/60 rounded-t-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-300 pb-[env(safe-area-inset-bottom)] ${
            expanded ? 'max-h-[80vh]' : 'max-h-[52vh]'
          }`}
          style={{
            transform: `translateY(${Math.max(dragOffset, 0)}px)`,
            transition: isDragging.current ? 'none' : 'transform 200ms ease-out, max-height 250ms ease-out',
          }}
        >
          {/* Drag handle */}
          <div
            className="flex justify-center pt-3 pb-2 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onClick={() => setExpanded((v) => !v)}
            role="button"
            aria-label="Drag to resize or dismiss"
          >
            <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          </div>
          {panelContent}
        </div>
      </>
    );
  }

  return (
    <div className="w-80 h-full flex-shrink-0 bg-card border-r border-border/60 flex flex-col overflow-hidden shadow-lg animate-in slide-in-from-left duration-200">
      {panelContent}
    </div>
  );
};
